import { Location } from '@angular/common';
import { Component, ElementRef, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { JsonApiError } from '@app/errors/json-api.error';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { PasswordValidationService } from '@app/services/password-validation.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { isNil } from 'lodash-es';

@Component({
    templateUrl: './store-password.template.html',
    styleUrls: ['../common.style.scss', '../common.responsive.scss', './store-password.style.scss'],
})
export class StorePasswordView implements OnInit {
    readonly minimumCharacterCheck = 10;

    hasMinimumCharacters = false;
    hasMinimumStrength = false;
    passwordsMatch = false;
    passwordStrengthScore = 0;
    passwordStrengthLabel = '';

    token: string;
    confirmPassword?: string;
    isLoading = false;
    authorized = false;
    activated = false;
    passwordStored = false;
    showPassword = false;

    password: FormControl<string | null> = new FormControl();
    isOnboarding = false;
    standardErrorFeatureFlag = false;

    constructor(
        protected auth: AuthService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected notify: NotifyService,
        protected location: Location,
        protected element: ElementRef,
        private featureService: FeatureService,
        private authService: AuthService,
        private passwordValidationService: PasswordValidationService
    ) {
        if (this.authService.activationToken) {
            this.token = this.authService.activationToken;
            this.isOnboarding = true;
            this.authorized = true;
            this.focusInput();
        } else {
            this.token = this.route.snapshot.queryParams['token'];
            this.authenticateByToken();
            this.activated = true;
        }
        this.featureService.has(FeatureFlag.standardErrorResponse).then((flagValue: boolean) => {
            this.standardErrorFeatureFlag = flagValue;
        });
    }

    ngOnInit(): void {}

    validatePassword(): void {
        const validation = this.passwordValidationService.validatePassword(this.password.value);

        this.hasMinimumCharacters = validation.hasMinimumLength;
        this.hasMinimumStrength = validation.hasMinimumStrength;
        this.passwordStrengthScore = validation.score;
        this.passwordStrengthLabel = this.passwordValidationService.getStrengthLabel(validation.score);

        this.setControlErrors();

        if (this.hasMinimumCharacters && this.hasMinimumStrength) {
            this.passwordsMatch =
                !isNil(this.password.value) &&
                !isNil(this.confirmPassword) &&
                this.password.value === this.confirmPassword;
        } else {
            this.passwordsMatch = false;
        }
    }

    savePassword(): void {
        if (!this.passwordIsValid()) {
            return;
        }

        this.isLoading = true;
        this.storePassword();
    }

    toggleShowPassword(): void {
        this.showPassword = !this.showPassword;
    }

    goBack(): void {
        this.location.back();
    }

    private setControlErrors(): void {
        const hasError = !this.hasMinimumCharacters || !this.hasMinimumStrength;

        const errors = hasError ? { invalid: true } : null;
        this.password.setErrors(errors);
    }

    private focusInput(): void {
        // Because the view render changes based on whether or not someone is authenticated or not, we need to wrap this in a timeout
        // to make sure angular has set all the flags first before we attempt to focus the input.
        setTimeout(() => {
            this.element.nativeElement.querySelector('input').focus();
        });
    }

    private async activateInvitation(): Promise<void> {
        if (this.activated) {
            return Promise.resolve();
        }

        try {
            await this.auth.activateInvitation();
            this.activated = true;
        } catch (err) {
            this.notify.error(ErrorParser.parse(err as Record<string, unknown>));
        }
    }

    private async storePassword(): Promise<void> {
        const errorMessage: TranslatableKey = 'unableToSetPassword';

        if (!this.password.value || !this.confirmPassword) {
            this.notify.error(errorMessage);
            return;
        }

        await this.activateInvitation();

        const oAuthResponse = await this.auth
            .storePassword({
                value: this.password.value,
                valueConfirmation: this.confirmPassword,
            })
            .catch((err: JsonApiError) => {
                const errorMap = ErrorParser.parseAsMap(err.raw.error);

                if ('value' in errorMap) {
                    this.password.setErrors({ custom: { message: errorMap.value } });
                }
            })
            .finally(() => {
                this.isLoading = false;
            });

        if (!oAuthResponse) {
            this.notify.error(errorMessage);
            return;
        }

        this.passwordStored = true;
    }

    private passwordIsValid(): boolean {
        return this.hasMinimumCharacters && this.hasMinimumStrength && this.passwordsMatch;
    }

    private authenticateByToken(): void {
        this.isLoading = true;
        this.auth
            .authenticateWithPasswordResetToken(this.token)
            .then(() => {
                this.isLoading = false;
                this.authorized = true;
                this.focusInput();
            })
            .catch((err) => {
                this.isLoading = false;
                this.authorized = false;
                if (this.standardErrorFeatureFlag) {
                    this.notify.error(ErrorParser.parseJsonApiError(err));
                } else {
                    this.notify.error(ErrorParser.parse(err));
                }
            });
    }
}
